// src/pages/PropertyDetails.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// V17 ENTERPRISE — Fix: extracción robusta de URLs desde objetos en images[]
// ═══════════════════════════════════════════════════════════════════════════════
// 🔥 V17: extractUrlFromObject() compartida — extrae URL de {url, src, data, public, secure_url, etc}
// 🔥 V17: loadImages() ahora detecta objetos complejos dentro del array
// 🔥 V17: isDirectUrl() ampliado para detectar objetos con URL interna
// 🔥 V17: hasValidImages() verifica también objetos con campos de URL
// 🔥 V17: getDisplayUrl() mejorado para Cloudinary sin protocolo
// 🔥 V16: FIX — fetchPropertyWithRetry prioriza previewImage sobre imágenes viejas
// 🔥 V16: FIX — loadImages usa previewImage como fallback principal
// ═══════════════════════════════════════════════════════════════════════════════

import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/useAuth";
import { useNotifications } from "../contexts/NotificationsContext";
import { useIsMobile } from "../hooks/useMediaQuery";
import DataContext from "../lib/data/context/DataContext";
import {
  ArrowLeft, MapPin, Users, ChevronLeft, ChevronRight,
  Download, Star, Check, Smartphone, Home, Trash2, Edit3,
  Camera, Maximize2, X, TrendingUp, CheckCircle, Phone,
  Mail, ImageOff, Loader, Cloud, Share2, RefreshCw, Eye,
  Zap, ChevronDown, Copy, MessageCircle, Clock,
} from "lucide-react";
import jsPDF from "jspdf";
import { getPublicPropertyUrl } from "../config/urls";
import { publishProperty } from "../services/database";
import { getOptimizedCloudinaryUrl } from "../config/imageConfig";

// ═══════════════════════════════════════════════════════════════════════════════
// 🛠️ UTILITARIOS
// ═══════════════════════════════════════════════════════════════════════════════

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function formatBRL(value) {
  if (!value && value !== 0) return "Valor não informado";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value));
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return [37, 99, 235];
  const clean = hex.replace("#", "").trim();
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
  const match = full.match(/^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  return match ? [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)] : [37, 99, 235];
}

function resolvePropertyId(rawId, database) {
  if (!rawId) return null;
  const syncManager = database?.syncManager || database?._syncManager || window.__syncManager;
  if (syncManager?.resolveRealId) {
    const resolved = syncManager.resolveRealId(rawId);
    if (resolved && resolved !== rawId) return resolved;
  }
  return rawId;
}

function isBlobUrl(url) {
  return typeof url === 'string' && url.startsWith('blob:');
}

function isHttpUrl(url) {
  return typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));
}

function isCloudinaryUrl(url) {
  return typeof url === 'string' && url.includes('res.cloudinary.com');
}

/**
 * 🔥 V17: Intenta extraer una URL usable de cualquier tipo de objeto
 * Soporta: {url}, {src}, {data}, {public}, {path}, {secure_url}, {downloadURL}
 */
function extractUrlFromObject(obj) {
  if (!obj || typeof obj !== 'object') return null;
  
  const fields = ['url', 'src', 'data', 'public', 'path', 'secure_url', 'downloadURL', 'location', 'href'];
  
  for (const field of fields) {
    const val = obj[field];
    if (typeof val === 'string' && val.length > 10) {
      if (val.includes('res.cloudinary.com') && !val.startsWith('http')) {
        return 'https://' + val.replace(/^\/+/, '');
      }
      return val;
    }
  }
  
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === 'string' && (val.startsWith('http') || val.startsWith('blob:') || val.startsWith('data:') || val.includes('res.cloudinary.com'))) {
      if (val.includes('res.cloudinary.com') && !val.startsWith('http')) {
        return 'https://' + val.replace(/^\/+/, '');
      }
      return val;
    }
  }
  
  return null;
}

/**
 * 🔥 V17: Detecta si un valor es una URL directa (string o objeto con URL)
 */
function isDirectUrl(value) {
  if (typeof value === 'string') {
    return value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:') || value.includes('res.cloudinary.com');
  }
  if (typeof value === 'object' && value !== null) {
    return extractUrlFromObject(value) !== null;
  }
  return false;
}

function getDisplayUrl(url, variant = 'fullscreen') {
  if (!url) return null;
  
  // Si es objeto, extraer URL
  if (typeof url === 'object' && url !== null) {
    const extracted = extractUrlFromObject(url);
    if (extracted) url = extracted;
    else return null;
  }
  
  if (typeof url !== 'string') return null;
  
  if (isBlobUrl(url)) return url;
  if (url.startsWith('data:')) return url;
  
  // Cloudinary sin protocolo
  if (isCloudinaryUrl(url) && !url.startsWith('http')) {
    url = 'https://' + url.replace(/^\/+/, '');
  }
  
  if (url.startsWith('http')) {
    try {
      return getOptimizedCloudinaryUrl(url, variant) || url;
    } catch {
      return url;
    }
  }
  return url;
}

/**
 * 🔥 V17: Determina si un conjunto de imágenes es "válido"
 * Ahora detecta objetos con URLs internas
 */
function hasValidImages(images) {
  if (!Array.isArray(images) || images.length === 0) return false;
  return images.some(img => isDirectUrl(img));
}

async function fetchPropertyWithRetry(database, rawId, userId, signal, maxRetries = 6) {
  const resolvedId = resolvePropertyId(rawId, database);

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (signal?.aborted) return { data: null, resolvedId };
    if (attempt > 0) {
      const delay = Math.min(200 * Math.pow(1.5, attempt - 1), 2000);
      await sleep(delay);
      if (signal?.aborted) return { data: null, resolvedId };
    }

    let data = await database?.getPropertyById?.(resolvedId);
    if (data && (hasValidImages(data.images) || data.previewImage)) {
      return { data, resolvedId };
    }
    if (data) return { data, resolvedId };

    if (rawId !== resolvedId) {
      data = await database?.getPropertyById?.(rawId);
      if (data && (hasValidImages(data.images) || data.previewImage)) {
        return { data, resolvedId: rawId };
      }
      if (data) return { data, resolvedId: rawId };
    }

    try {
      const uid = userId || database?.userId;
      if (uid) {
        const stored = JSON.parse(localStorage.getItem(`imoveispro_db_user_${uid}`) || "{}");
        const props = Array.isArray(stored.properties) ? stored.properties : [];
        const found = props.find(
          (p) =>
            String(p.id) === String(resolvedId) ||
            String(p.id) === String(rawId) ||
            p._cloudId === resolvedId ||
            p._cloudId === rawId ||
            p._mergedFrom === rawId
        );
        if (found) {
          if (found.previewImage && !hasValidImages(found.images)) {
            found.images = [found.previewImage];
          }
          return { data: found, resolvedId: found.id };
        }
      }
    } catch (_) {}

    if (attempt < maxRetries - 1) {
      const syncManager = database?.syncManager || database?._syncManager || window.__syncManager;
      if (syncManager?._waitForRealId && rawId !== resolvedId) {
        const mapped = await syncManager._waitForRealId(rawId, 1000);
        if (mapped) {
          const d = await database?.getPropertyById?.(mapped);
          if (d) return { data: d, resolvedId: mapped };
        }
      }
    }
  }
  return { data: null, resolvedId };
}

function buildPublicLink(property) {
  if (!property?.id) return typeof window !== "undefined" ? `${window.location.origin}/imoveis` : "/imoveis";
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const params = new URLSearchParams();
  const cid = property.companyId || property.company;
  if (cid) params.append("cid", cid);
  const uid = property.userId || property.createdBy;
  if (uid) params.append("uid", uid);
  const qs = params.toString();
  return `${baseUrl}/imovel/${property.id}${qs ? `?${qs}` : ""}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// 💀 SKELETON
// ═══════════════════════════════════════════════════════════════════════════════

function PropertySkeleton({ isMobile }) {
  return (
    <div className={isMobile ? "p-3 space-y-4" : "p-6 max-w-7xl mx-auto space-y-8"}>
      <div className="flex justify-between items-center">
        <div className="h-8 w-24 bg-slate-200 rounded-lg animate-pulse" />
        <div className="h-8 w-32 bg-slate-200 rounded-lg animate-pulse" />
      </div>
      <div className="h-24 rounded-2xl bg-slate-200 animate-pulse" />
      <div className="aspect-[16/9] rounded-2xl bg-slate-200 animate-pulse" />
      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-6`}>
        <div className="h-48 rounded-2xl bg-slate-200 animate-pulse" />
        <div className="h-48 rounded-2xl bg-slate-200 animate-pulse" />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 🖼️ FULLSCREEN GALLERY
// ═══════════════════════════════════════════════════════════════════════════════

const FullscreenGallery = ({ images, initialIndex, isOpen, onClose }) => {
  const [idx, setIdx] = useState(initialIndex);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  
  useEffect(() => { 
    setIdx(initialIndex); 
    setImgLoading(true); 
    setImgError(false); 
  }, [initialIndex]);
  
  const prev = useCallback(
    () => { setImgLoading(true); setImgError(false); setIdx((i) => (i === 0 ? images.length - 1 : i - 1)); },
    [images.length]
  );
  const next = useCallback(
    () => { setImgLoading(true); setImgError(false); setIdx((i) => (i === images.length - 1 ? 0 : i + 1)); },
    [images.length]
  );
  
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, prev, next]);
  
  if (!isOpen || !images?.length) return null;
  
  const src = getDisplayUrl(images[idx], "fullscreen");
  const isBlob = isBlobUrl(typeof images[idx] === 'string' ? images[idx] : extractUrlFromObject(images[idx]) || '');
  
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100000] bg-black flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Galeria de fotos"
    >
      {/* Safe-area chrome */}
      <button
        onClick={onClose}
        className="absolute z-20 w-11 h-11 flex items-center justify-center bg-white/12 hover:bg-white/20 active:scale-95 rounded-full text-white backdrop-blur-md"
        style={{ top: "max(12px, env(safe-area-inset-top))", right: "max(12px, env(safe-area-inset-right))" }}
        aria-label="Fechar"
      >
        <X className="w-5 h-5" />
      </button>
      <div
        className="absolute z-20 px-3.5 py-1.5 bg-white/12 backdrop-blur-md rounded-full text-white text-sm font-semibold tabular-nums"
        style={{ top: "max(16px, env(safe-area-inset-top))", left: "max(12px, env(safe-area-inset-left))" }}
      >
        {idx + 1} / {images.length}
      </div>
      
      {isBlob && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-amber-500/90 backdrop-blur-sm rounded-full text-white text-xs z-20 flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Pendente de upload
        </div>
      )}
      
      <motion.div
        key={idx}
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full h-full flex items-center justify-center p-4 sm:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        {imgLoading && !imgError && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Loader className="w-10 h-10 text-white/60 animate-spin" />
          </div>
        )}
        {!imgError && src ? (
          <img 
            src={src} 
            alt={`Foto ${idx + 1}`}
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
            decoding="async"
            onLoad={() => setImgLoading(false)} 
            onError={() => { setImgError(true); setImgLoading(false); }}
          />
        ) : imgError ? (
          <div className="flex flex-col items-center gap-3 text-white/60">
            <ImageOff className="w-14 h-14" />
            <p className="text-sm">Erro ao carregar imagem</p>
          </div>
        ) : null}
      </motion.div>
      
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/12 hover:bg-white/20 active:scale-95 rounded-full z-20 backdrop-blur-md"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/12 hover:bg-white/20 active:scale-95 rounded-full z-20 backdrop-blur-md"
            aria-label="Próxima"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* Dots mobile */}
      {images.length > 1 && images.length <= 12 && (
        <div
          className="absolute left-0 right-0 flex justify-center gap-1.5 z-20 sm:hidden"
          style={{ bottom: "max(16px, env(safe-area-inset-bottom))" }}
        >
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setImgLoading(true); setImgError(false); setIdx(i); }}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/40"}`}
              aria-label={`Foto ${i + 1}`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🖼️ IMAGE GALLERY
// ═══════════════════════════════════════════════════════════════════════════════

const ImageGallery = ({ images, activeImage, onPrev, onNext, onSelect, primaryColor, onAddImage, isMobile }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [mainLoading, setMainLoading] = useState(true);
  const [mainError, setMainError] = useState(false);
  
  const currentImage = images?.[activeImage];
  const currentRaw = typeof currentImage === 'string' ? currentImage : extractUrlFromObject(currentImage) || '';
  const isCurrentBlob = isBlobUrl(currentRaw);
  const isCurrentCloudinary = isCloudinaryUrl(currentRaw);
  
  const currentSrc = useMemo(
    () => currentImage ? getDisplayUrl(currentImage, isMobile ? "preview" : "fullscreen") : null,
    [currentImage, isMobile]
  );
  
  useEffect(() => { setMainLoading(true); setMainError(false); }, [activeImage]);

  if (!images?.length) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="aspect-[4/3] sm:aspect-[16/9] rounded-2xl bg-muted border border-color flex flex-col items-center justify-center text-secondary cursor-pointer group active:scale-[0.99] transition"
        onClick={onAddImage}
      >
        <div className="w-16 h-16 rounded-2xl bg-card border border-color flex items-center justify-center mb-3 shadow-sm group-hover:scale-105 transition-transform">
          <Camera className="w-8 h-8 text-muted" />
        </div>
        <p className="font-semibold text-primary">Nenhuma imagem</p>
        <p className="text-muted mt-1 text-sm">Toque para adicionar fotos</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3">
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
        className="relative aspect-[4/3] sm:aspect-[16/9] rounded-2xl overflow-hidden bg-black group shadow-card"
      >
        {mainLoading && !mainError && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
            <Loader className="w-10 h-10 text-white/60 animate-spin" />
          </div>
        )}
        {!mainError && currentSrc ? (
          <img
            src={currentSrc}
            alt={`Foto ${activeImage + 1}`}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02] cursor-zoom-in ${mainLoading ? "opacity-0" : "opacity-100"}`}
            decoding="async"
            onLoad={() => setMainLoading(false)}
            onError={() => { setMainError(true); setMainLoading(false); }}
            onClick={() => setFullscreen(true)}
          />
        ) : mainError ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-900">
            <ImageOff className="w-12 h-12 text-slate-400 mb-2" />
            <p className="text-slate-400 text-sm">Erro ao carregar imagem</p>
          </div>
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10 pointer-events-none" />
        
        {/* Counter top-right */}
        <div className="absolute top-3 right-3 z-10 px-2.5 py-1 bg-black/50 backdrop-blur-md rounded-full text-white text-xs font-semibold tabular-nums">
          {activeImage + 1} / {images.length}
        </div>
        
        {/* Status chip bottom-left */}
        {(isCurrentCloudinary || isCurrentBlob) && !mainLoading && !mainError && (
          <div className={`absolute bottom-3 left-3 z-10 px-2 py-1 backdrop-blur-md rounded-full text-white text-[10px] font-semibold flex items-center gap-1 ${isCurrentBlob ? "bg-amber-500/85" : "bg-black/45"}`}>
            {isCurrentBlob ? <Clock className="w-3 h-3" /> : <Cloud className="w-3 h-3" />}
            {isCurrentBlob ? "Pendente" : "CDN"}
          </div>
        )}
        
        {/* Nav arrows — always visible on mobile, hover on desktop */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={onPrev}
              className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-black/45 hover:bg-black/65 active:scale-95 rounded-full text-white backdrop-blur-sm transition ${isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onNext}
              className={`absolute right-2.5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-black/45 hover:bg-black/65 active:scale-95 rounded-full text-white backdrop-blur-sm transition ${isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              aria-label="Próxima"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setFullscreen(true)}
          className="absolute top-3 left-3 z-10 w-10 h-10 flex items-center justify-center bg-black/45 hover:bg-black/65 active:scale-95 rounded-full text-white backdrop-blur-sm"
          aria-label="Tela cheia"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </motion.div>
      
      {/* Thumbnails — mobile strip + desktop */}
      {images.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5 scroll-smooth"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {images.map((img, i) => {
            const thumb = getDisplayUrl(img, "thumbnail");
            const rawUrl = typeof img === 'string' ? img : extractUrlFromObject(img) || '';
            const isThumbBlob = isBlobUrl(rawUrl);
            const active = activeImage === i;
            
            return (
              <button
                key={`thumb-${i}-${String(thumb || i).slice(-20)}`}
                type="button"
                onClick={() => onSelect(i)}
                className={`relative flex-shrink-0 overflow-hidden rounded-xl transition-all ${
                  isMobile ? "w-[72px] h-[54px]" : "w-24 h-16"
                } ${active ? "ring-2 ring-offset-2 ring-offset-[var(--bg-primary)] opacity-100 scale-[1.02]" : "opacity-70 hover:opacity-100"}`}
                style={active ? { boxShadow: `0 0 0 2px ${primaryColor}` } : undefined}
                aria-label={`Selecionar foto ${i + 1}`}
                aria-current={active}
              >
                {thumb ? (
                  <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <ImageOff className="w-4 h-4 text-muted" />
                  </div>
                )}
                {isThumbBlob && (
                  <div className="absolute bottom-0.5 right-0.5 bg-amber-500/90 rounded-full p-0.5">
                    <Clock className="w-2 h-2 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </motion.div>
      )}
      
      <AnimatePresence>
        {fullscreen && (
          <FullscreenGallery images={images} initialIndex={activeImage} isOpen={fullscreen} onClose={() => setFullscreen(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🧩 MODAIS Y SUB-COMPONENTES
// ═══════════════════════════════════════════════════════════════════════════════

function ModalShell({ title, tone = "primary", onClose, children, primaryColor = "#2563eb", isMobile }) {
  const color = tone === "danger" ? "#e11d48" : tone === "warning" ? "#f59e0b" : primaryColor;
  return (
    <div className={`fixed inset-0 z-[10000] flex ${isMobile ? "items-end" : "items-center"} justify-center p-0 sm:p-4`}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
        animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
        exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
        transition={{ type: "spring", stiffness: 420, damping: 36 }}
        className={`relative w-full max-w-md bg-modal border border-color shadow-xl overflow-hidden ${isMobile ? "rounded-t-[22px] max-h-[88dvh]" : "rounded-2xl"}`}
        style={{ paddingBottom: isMobile ? "max(8px, env(safe-area-inset-bottom))" : undefined }}
      >
        {isMobile && (
          <div className="flex justify-center pt-2.5">
            <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
          </div>
        )}
        <div className={`px-4 py-3 text-white font-semibold flex items-center justify-between ${isMobile ? "text-sm" : ""}`}
          style={{ backgroundColor: color }}>
          {title}
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className={`${isMobile ? "p-4" : "p-5"} space-y-4`}>{children}</div>
      </motion.div>
    </div>
  );
}

function ClientPickerModal({ open, onClose, onSelect, clients, primaryColor, isMobile, title = "Selecionar Cliente" }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  useEffect(() => { if (open) { setSearch(""); setSelected(null); } }, [open]);
  const filtered = useMemo(
    () => clients.filter((c) =>
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.phone?.includes(search)
    ),
    [clients, search]
  );
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}>
        <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}>
          <div className={`${isMobile ? "p-4" : "p-6"} border-b border-gray-100 flex justify-between items-center`}>
            <h3 className={`font-bold flex items-center gap-2 ${isMobile ? "text-lg" : "text-xl"}`}>
              <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${primaryColor}20` }}>
                <Users size={18} style={{ color: primaryColor }} />
              </div>
              {title}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
          <div className={isMobile ? "p-4" : "p-6"}>
            <input type="text" placeholder="Buscar cliente..."
              className="w-full px-4 py-3 border rounded-xl focus:ring-2 outline-none text-sm mb-4"
              value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
            <div className={`${isMobile ? "max-h-60" : "max-h-80"} overflow-y-auto space-y-2`}>
              {filtered.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">Nenhum cliente encontrado</p>
              ) : filtered.map((client) => (
                <motion.div key={client.id} whileHover={{ x: 4 }}
                  onClick={() => setSelected(client.id === selected?.id ? null : client)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selected?.id === client.id ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"}`}
                  style={selected?.id === client.id ? { borderColor: primaryColor } : {}}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {client.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{client.name}</div>
                      <div className="text-sm text-gray-500 truncate">{client.email}</div>
                    </div>
                    {selected?.id === client.id && <CheckCircle size={20} style={{ color: primaryColor }} />}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <div className={`${isMobile ? "p-4" : "p-6"} bg-gray-50 flex justify-end gap-3`}>
            <button onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-gray-100 text-sm">Cancelar</button>
            <button onClick={() => { if (selected) { onSelect(selected); onClose(); } }} disabled={!selected}
              className={`px-4 py-2 rounded-lg text-white text-sm transition-all ${selected ? "hover:opacity-90" : "opacity-50 cursor-not-allowed"}`}
              style={{ backgroundColor: primaryColor }}>
              Confirmar
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function SaleConfirmationModal({ open, onClose, onConfirm, property, client, primaryColor, isMobile, processing }) {
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}>
        <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}>
          <div className={`${isMobile ? "p-4" : "p-6"} border-b border-gray-100 flex justify-between items-center`}>
            <h3 className={`font-bold flex items-center gap-2 ${isMobile ? "text-lg" : "text-xl"}`}>
              <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${primaryColor}20` }}>
                <TrendingUp size={18} style={{ color: primaryColor }} />
              </div>
              Confirmar Venda
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
          <div className={isMobile ? "p-4" : "p-6"}>
            <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200 mb-4 text-emerald-700 text-sm">
              Ao confirmar, o imóvel será marcado como <strong>Vendido</strong>.
            </div>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Imóvel</span>
                <span className="font-medium text-sm text-gray-900 truncate ml-4">{property?.title}</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Valor</span>
                <span className="font-medium text-sm text-emerald-600">{formatBRL(property?.price)}</span>
              </div>
              {client && (
                <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Comprador</span>
                  <span className="font-medium text-sm text-gray-900">{client.name}</span>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} disabled={processing}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 disabled:opacity-50">
                Cancelar
              </button>
              <button onClick={onConfirm} disabled={processing}
                className="flex-1 px-4 py-3 rounded-xl text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-70"
                style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}>
                {processing && <Loader className="w-4 h-4 animate-spin" />}
                {processing ? "Salvando..." : "Confirmar Venda"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ActionBtn({ icon: Icon, label, onClick, color }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all active:scale-95 text-white ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function InfoCard({ icon: Icon, primaryColor, isMobile, children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className={isMobile ? "p-4" : "p-6"}>
        <div className="flex items-start gap-4">
          <div className={`${isMobile ? "p-2" : "p-3"} rounded-xl flex-shrink-0`}
            style={{ backgroundColor: primaryColor + "20" }}>
            <Icon className={isMobile ? "w-4 h-4" : "w-5 h-5"} style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">{children}</div>
        </div>
      </div>
    </motion.div>
  );
}

function MobileActionsMenu({ canSell, property, copied, handleShare, toggleFeatured, handleSellClick, handleWhatsApp, exportPDF, handleCopyLink }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [open]);
  const actions = [
    { icon: copied ? Check : Copy, label: copied ? "Copiado" : "Copiar Link", onClick: handleCopyLink, color: "text-blue-500" },
    { icon: Share2, label: "Compartilhar", onClick: handleShare, color: "text-blue-500" },
    { icon: Star, label: property?.featured ? "Remover destaque" : "Destacar", onClick: toggleFeatured, color: property?.featured ? "text-yellow-500" : "text-slate-500" },
    ...(canSell ? [{ icon: TrendingUp, label: "Registrar Venda", onClick: handleSellClick, color: "text-emerald-500" }] : []),
    { icon: MessageCircle, label: "WhatsApp", onClick: handleWhatsApp, color: "text-green-500" },
    { icon: Download, label: "Exportar PDF", onClick: exportPDF, color: "text-indigo-500" },
  ];
  return (
    <div className="relative" ref={menuRef}>
      <button onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white font-semibold text-sm active:scale-95 transition-all">
        <Zap size={15} />
        Ações
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="absolute top-full right-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50">
            {actions.map((action, i) => (
              <button key={i} onClick={() => { action.onClick(); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors ${i > 0 ? "border-t border-slate-100" : ""}`}>
                <action.icon size={17} className={action.color} />
                {action.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 🏠 COMPONENTE PRINCIPAL PropertyDetails V17
// ═══════════════════════════════════════════════════════════════════════════════

export default function PropertyDetails() {
  const { id: rawId } = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();
  const { database, company, user } = useAuth();
  const isMobile      = useIsMobile();

  const authUserId = useMemo(() => {
    try {
      const stored = localStorage.getItem("auth_user");
      return stored ? JSON.parse(stored).id : user?.uid || null;
    } catch { return user?.uid || null; }
  }, [user?.uid]);

  const ctxRef = useRef(null);
  const getContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    try {
      if (authUserId && DataContext?.getInstance) {
        ctxRef.current = DataContext.getInstance(authUserId);
        return ctxRef.current;
      }
    } catch (_) {}
    return null;
  }, [authUserId]);

  const notifications  = useNotifications?.() ?? {};
  const notifyFnRef    = useRef(notifications?.notifySystem || notifications?.notify || (() => {}));
  useEffect(() => {
    notifyFnRef.current = notifications?.notifySystem || notifications?.notify || (() => {});
  }, [notifications]);
  const notifySystem = useCallback((...args) => notifyFnRef.current(...args), []);

  const [property,            setProperty]          = useState(null);
  const [loading,             setLoading]           = useState(true);
  const [loadError,           setLoadError]         = useState(false);
  const [activeImage,         setActiveImage]       = useState(0);
  const [copied,              setCopied]            = useState(false);
  const [processing,          setProcessing]        = useState(false);
  const [deleting,            setDeleting]          = useState(false);
  const [showDeleteModal,     setShowDeleteModal]   = useState(false);
  const [showSaleModal,       setShowSaleModal]     = useState(false);
  const [showClientModal,     setShowClientModal]   = useState(false);
  const [showLinkClientModal, setShowLinkClientModal] = useState(false);
  const [selectedClient,      setSelectedClient]    = useState(null);
  const [clients,             setClients]           = useState([]);
  const [linkedClientsList,   setLinkedClientsList] = useState([]);

  const abortRef    = useRef({ aborted: false });
  const databaseRef = useRef(database);
  useEffect(() => { databaseRef.current = database; }, [database]);

  const effectiveRawId = useMemo(() => {
    if (rawId) return rawId;
    return sessionStorage.getItem("last_created_property_id") || null;
  }, [rawId]);

  const resolvedId = property?._resolvedId || property?.id || rawId;

  // ── loadImages V17: extrae URLs de objetos dentro del array ─────────────────
  const loadImages = useCallback(async (rawImages, previewImage = null) => {
    // Si hay previewImage y el array está vacío o tiene imágenes inválidas
    if (previewImage && isDirectUrl(previewImage) && !hasValidImages(rawImages)) {
      return [typeof previewImage === 'string' ? previewImage : extractUrlFromObject(previewImage) || previewImage];
    }
    
    if (!rawImages?.length) {
      if (previewImage && isDirectUrl(previewImage)) {
        return [typeof previewImage === 'string' ? previewImage : extractUrlFromObject(previewImage) || previewImage];
      }
      return [];
    }
    
    const db = databaseRef.current;
    const results = [];
    
    for (const img of rawImages) {
      // String directa (HTTP, blob, data, Cloudinary)
      if (typeof img === 'string') {
        if (isDirectUrl(img)) {
          results.push(img);
        } else if (img.startsWith("img_") || img.startsWith("cloud_")) {
          // ID interno → resolver desde DB
          try {
            const imgData = await db?.getImageById?.(img);
            if (imgData?.data) results.push(imgData.data);
            else if (imgData?.url) results.push(imgData.url);
          } catch (_) {}
        } else if (img.length > 5) {
          // String no reconocido pero no vacío → intentar usarlo
          results.push(img);
        }
      }
      // Objeto con campos de URL
      else if (typeof img === 'object' && img !== null) {
        const extracted = extractUrlFromObject(img);
        if (extracted) {
          results.push(extracted);
        }
      }
    }
    
    // Si después de procesar no hay imágenes válidas, usar previewImage
    if (results.length === 0 && previewImage && isDirectUrl(previewImage)) {
      const prevUrl = typeof previewImage === 'string' ? previewImage : extractUrlFromObject(previewImage);
      if (prevUrl) return [prevUrl];
    }
    
    return results;
  }, []);

  // ── loadLinkedClients ─────────────────────────────────────────────────────
  const loadLinkedClients = useCallback(async (prop) => {
    if (!prop) { setLinkedClientsList([]); return; }
    const clientIds = prop.linkedClients || (prop.clientId ? [prop.clientId] : []);
    if (!clientIds.length) { setLinkedClientsList([]); return; }
    const ctx    = getContext();
    const db     = databaseRef.current;
    const loaded = [];
    for (const cid of clientIds) {
      let c = null;
      try {
        if (ctx?.collections?.clients?.findById) c = await ctx.collections.clients.findById(cid);
      } catch (_) {}
      if (!c) c = await db?.getClientById?.(cid);
      if (c) loaded.push(c);
    }
    setLinkedClientsList(loaded);
  }, [getContext]);

  // ── loadProperty ──────────────────────────────────────────────────────────
  const loadProperty = useCallback(async () => {
    if (!effectiveRawId) {
      setLoading(false);
      setLoadError(true);
      return;
    }

    const db = databaseRef.current;
    if (!db) {
      setLoading(false);
      setLoadError(true);
      return;
    }

    abortRef.current = { aborted: false };
    const signal = abortRef.current;

    setLoading(true);
    setLoadError(false);

    const { data, resolvedId: rid } = await fetchPropertyWithRetry(
      db,
      effectiveRawId,
      authUserId,
      signal
    );

    if (signal.aborted) return;

    if (!data) {
      setLoadError(true);
      setLoading(false);
      return;
    }

    sessionStorage.removeItem("last_created_property_id");

    const images = await loadImages(data.images || [], data.previewImage || null);

    let allClients = [];
    const ctx = getContext();
    try {
      if (ctx?.collections?.clients?.read) {
        allClients = await ctx.collections.clients.read();
      }
    } catch (_) {}
    if (!Array.isArray(allClients) || !allClients.length) {
      const raw = await db?.getClients?.();
      allClients = Array.isArray(raw) ? raw : [];
    }

    if (signal.aborted) return;

    const prop = { ...data, images, _resolvedId: rid };
    setClients(allClients.filter((c) => c && c.pipeline !== "Fechado"));
    setProperty(prop);
    setActiveImage(0);
    setLoading(false);
    await loadLinkedClients(prop);
  }, [effectiveRawId, authUserId, loadImages, loadLinkedClients, getContext]);

  useEffect(() => {
    loadProperty();
    return () => { abortRef.current.aborted = true; };
  }, [loadProperty]);

  useEffect(() => {
    if (!resolvedId) return;
    const ctx = getContext();
    if (!ctx || typeof ctx.subscribe !== "function") return;

    const unsubscribe = ctx.subscribe((newState) => {
      if (newState?.properties && property) {
        const updated = newState.properties.find(
          (p) => String(p.id) === String(resolvedId) || String(p.id) === String(property.id)
        );
        if (updated && JSON.stringify(updated) !== JSON.stringify(property)) {
          const updatedImages = hasValidImages(updated.images) ? updated.images : (hasValidImages(property.images) ? property.images : [updated.previewImage].filter(Boolean));
          setProperty((prev) =>
            prev ? { ...prev, ...updated, images: updatedImages, _resolvedId: resolvedId } : prev
          );
        }
      }
      if (newState?.clients && linkedClientsList.length > 0) {
        const updatedClients = linkedClientsList.map((lc) => {
          const found = newState.clients.find((c) => String(c.id) === String(lc.id));
          return found && JSON.stringify(found) !== JSON.stringify(lc) ? found : lc;
        });
        if (JSON.stringify(updatedClients) !== JSON.stringify(linkedClientsList)) {
          setLinkedClientsList(updatedClients);
        }
      }
    });

    return () => { if (typeof unsubscribe === "function") unsubscribe(); };
  }, [resolvedId, property?.id, getContext]);

  useEffect(() => {
    if (!effectiveRawId && !loading) {
      notifySystem({ type: "warning", title: "Imóvel não encontrado" });
      navigate("/admin/imoveis");
    }
  }, [effectiveRawId, loading, navigate, notifySystem]);

  // ── Derivados ─────────────────────────────────────────────────────────────
  const primaryColor      = company?.cor || "#2563eb";
  const priceFormatted    = useMemo(() => formatBRL(property?.price), [property?.price]);
  const iptuFormatted     = useMemo(() => property?.iptu ? formatBRL(property.iptu) : null, [property?.iptu]);
  const condoFeeFormatted = useMemo(() => property?.condoFee ? formatBRL(property.condoFee) : null, [property?.condoFee]);
  const linkedClient      = linkedClientsList[0] || null;
  const publicLink        = useMemo(() => property ? buildPublicLink(property) : "", [property]);
  const canSell           = useMemo(() => property?.status !== "Vendido", [property?.status]);

  const navigateBack = useCallback(() => {
    if (location.key !== "default") navigate(-1);
    else navigate("/admin/imoveis");
  }, [navigate, location.key]);

  const prevImage = useCallback(
    () => setActiveImage((i) => (i === 0 ? (property?.images?.length ?? 1) - 1 : i - 1)),
    [property?.images?.length]
  );
  const nextImage = useCallback(
    () => setActiveImage((i) => (i === (property?.images?.length ?? 1) - 1 ? 0 : i + 1)),
    [property?.images?.length]
  );

  const refreshProperty = useCallback(async () => {
    if (!resolvedId) return;
    const db  = databaseRef.current;
    const ctx = getContext();
    let updated = null;
    try {
      if (ctx?.collections?.properties?.findById) updated = await ctx.collections.properties.findById(resolvedId);
    } catch (_) {}
    if (!updated) updated = await db?.getPropertyById?.(resolvedId);
    if (!updated) return;
    const images = await loadImages(updated.images || [], updated.previewImage || null);
    const prop   = { ...updated, images, _resolvedId: resolvedId };
    setProperty((prev) => ({ ...prev, ...prop }));
    await loadLinkedClients(prop);
  }, [resolvedId, loadImages, loadLinkedClients, getContext]);

  const toggleFeatured = useCallback(async () => {
    if (!property) return;
    const db  = databaseRef.current;
    const ctx = getContext();
    const newFeatured = !property.featured;
    setProperty((prev) => prev ? { ...prev, featured: newFeatured } : prev);
    try {
      if (ctx?.collections?.properties?.update) {
        await ctx.collections.properties.update(resolvedId, { featured: newFeatured, updatedAt: new Date().toISOString() });
        if (ctx.syncManager) ctx.syncManager.markRecentlyModified(resolvedId);
      } else {
        await db?.updateProperty?.(resolvedId, { featured: newFeatured });
      }
    } catch (_) {
      setProperty((prev) => prev ? { ...prev, featured: property.featured } : prev);
    }
    notifySystem({ type: "success", title: newFeatured ? "Imóvel em destaque" : "Destaque removido" });
  }, [property, resolvedId, notifySystem, getContext]);

  const handleShare = useCallback(async () => {
    if (!property || !company) return;
    try {
      publishProperty(property, company);
      await navigator.clipboard.writeText(publicLink).catch(() => {});
      setCopied(true);
      notifySystem({ type: "success", title: "\u2705 Link copiado!", message: "Imóvel disponível publicamente." });
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      notifySystem({ type: "danger", title: "Erro ao compartilhar" });
    }
  }, [property, company, publicLink, notifySystem]);

  const handleCopyLink = useCallback(async () => {
    if (!property) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopied(true);
      notifySystem({ type: "success", title: "\u2705 Link copiado!" });
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      notifySystem({ type: "danger", title: "Erro ao copiar link" });
    }
  }, [property, publicLink, notifySystem]);

  const handleWhatsApp = useCallback(() => {
    if (!property) return;
    const msg = encodeURIComponent(
      `\u{1F3E1} *${property.title}*\n` +
      `\u{1F4B0} *${priceFormatted}*\n` +
      `\u{1F4CD} ${property.address || ""}${property.city ? `, ${property.city}` : ""}\n\n` +
      `\u{1F4F8} Veja todas as fotos:\n${publicLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  }, [property, priceFormatted, publicLink]);

  const exportPDF = useCallback(async () => {
    if (!property) return;
    const doc       = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin    = 14;
    let y           = margin;
    const rgb       = hexToRgb(company?.cor || "#2563eb");
    doc.setFillColor(...rgb); doc.rect(0, 0, pageWidth, 35, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("times", "bold"); doc.setFontSize(18);
    doc.text(company?.nome || "Imóveis Pro", margin, 15);
    doc.setFontSize(10); doc.setFont("times", "normal");
    if (company?.telefone) doc.text(company.telefone, margin, 24);
    if (company?.email)    doc.text(company.email,    margin, 30);
    y = 42;
    if (property.images?.length) {
      try {
        const imgUrl = getDisplayUrl(property.images[0], { width: 600, height: 400 });
        if (imgUrl && !isBlobUrl(imgUrl)) {
          const imgB64 = await fetch(imgUrl).then((r) => r.blob()).then((b) => new Promise((resolve) => {
            const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.readAsDataURL(b);
          }));
          doc.addImage(imgB64, "JPEG", margin, y, pageWidth - margin * 2, 80); y += 85;
        }
      } catch (_) {}
    }
    doc.setTextColor(30, 30, 30); doc.setFont("times", "bold"); doc.setFontSize(16);
    doc.text(property.title || "Imóvel", margin, y); y += 8;
    doc.setFont("times", "normal"); doc.setFontSize(12); doc.setTextColor(80, 80, 80);
    if (property.address)      { doc.text(`Endereco: ${property.address}`, margin, y);    y += 6; }
    if (property.neighborhood) { doc.text(`Bairro: ${property.neighborhood}`, margin, y); y += 6; }
    if (property.city)         { doc.text(`Cidade: ${property.city}`, margin, y);         y += 6; }
    y += 4;
    doc.setFillColor(...rgb); doc.rect(margin, y, pageWidth - margin * 2, 12, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("times", "bold"); doc.setFontSize(14);
    const preco = property.price ? Number(property.price).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "Sob consulta";
    doc.text(`Valor: ${preco}`, margin + 4, y + 8); y += 18;
    const specs = [
      property.type      ? ["Tipo",       String(property.type)]      : null,
      property.bedrooms  ? ["Quartos",    String(property.bedrooms)]  : null,
      property.bathrooms ? ["Banheiros",  String(property.bathrooms)] : null,
      property.area      ? ["Area",       `${property.area}m2`]       : null,
      property.garage    ? ["Garagem",    String(property.garage)]    : null,
      property.code      ? ["Codigo",     String(property.code)]      : null,
      property.iptu      ? ["IPTU",       Number(property.iptu).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })] : null,
      property.condoFee  ? ["Condominio", Number(property.condoFee).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })] : null,
    ].filter(Boolean);
    doc.setTextColor(30, 30, 30); doc.setFont("times", "bold"); doc.setFontSize(13);
    doc.text("Caracteristicas", margin, y); y += 8;
    if (specs.length > 0) {
      const colWidth = (pageWidth - margin * 2) / 2;
      specs.forEach((spec, i) => {
        const col = i % 2, row = Math.floor(i / 2);
        const specY = y + row * 7, specX = margin + col * colWidth;
        doc.setFont("times", "bold"); doc.setFontSize(10); doc.setTextColor(rgb[0], rgb[1], rgb[2]);
        doc.text(spec[0], specX, specY);
        doc.setFont("times", "normal"); doc.setTextColor(60, 60, 60);
        doc.text(String(spec[1]), specX + 28, specY);
      });
      y += Math.ceil(specs.length / 2) * 7 + 8;
    }
    if (property.description) {
      if (y > 220) { doc.addPage(); y = margin; }
      doc.setFont("times", "bold"); doc.setFontSize(13); doc.setTextColor(30, 30, 30);
      doc.text("Descricao", margin, y); y += 7;
      doc.setFont("times", "normal"); doc.setFontSize(10); doc.setTextColor(80, 80, 80);
      const lines = doc.splitTextToSize(property.description, pageWidth - margin * 2);
      doc.text(lines, margin, y); y += lines.length * 5 + 6;
    }
    if (y > 240) { doc.addPage(); y = margin; }
    doc.setFillColor(245, 245, 245); doc.rect(margin, y, pageWidth - margin * 2, 28, "F");
    doc.setFont("times", "bold"); doc.setFontSize(12); doc.setTextColor(30, 30, 30);
    doc.text("Corretor", margin + 4, y + 8);
    doc.setFont("times", "normal"); doc.setFontSize(10); doc.setTextColor(80, 80, 80);
    doc.text(user?.name || "Corretor", margin + 4, y + 16);
    if (user?.phone) doc.text(`Tel: ${user.phone}`, margin + 4,  y + 22);
    if (user?.email) doc.text(`Email: ${user.email}`, margin + 80, y + 16);
    y += 33;
    try {
      const publicUrl = buildPublicLink(property);
      const QRCode    = (await import("qrcode")).default;
      const qrDataUrl = await QRCode.toDataURL(publicUrl, { width: 80, margin: 1 });
      doc.addImage(qrDataUrl, "PNG", margin, y, 25, 25);
      doc.setFontSize(8); doc.setTextColor(100, 100, 100);
      doc.text("Escaneie para ver online", margin + 28, y + 12);
      doc.text(publicUrl, margin + 28, y + 20);
    } catch (_) {}
    doc.setFontSize(8); doc.setTextColor(150, 150, 150);
    doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")} as ${new Date().toLocaleTimeString("pt-BR")} - ${company?.nome || "Imoveis Pro"}`, margin, 285);
    doc.save(`imovel-${property.title || "propriedade"}.pdf`);
    notifySystem({ type: "success", title: "PDF gerado com sucesso!" });
  }, [property, company, user, notifySystem]);

  const confirmDelete = useCallback(async () => {
    if (!property) return;
    setDeleting(true);
    try {
      const db  = databaseRef.current;
      const ctx = getContext();
      if (ctx?.collections?.properties?.delete) {
        await ctx.collections.properties.delete(resolvedId);
      } else {
        await db?.deleteProperty?.(resolvedId);
      }
      notifySystem({ type: "success", title: "Imóvel excluído" });
      navigate("/admin/imoveis");
    } catch (_) {
      notifySystem({ type: "danger", title: "Erro ao excluir. Tente novamente." });
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  }, [resolvedId, navigate, notifySystem, property, getContext]);

  const handleSellClick = useCallback(() => {
    if (!property) return;
    if (linkedClient) { setSelectedClient(linkedClient); setShowSaleModal(true); }
    else              { setShowClientModal(true); }
  }, [property, linkedClient]);

  const handleClientSelected = useCallback((client) => {
    setSelectedClient(client);
    setShowClientModal(false);
    setShowSaleModal(true);
  }, []);

  const handleConfirmSale = useCallback(async () => {
    if (!property || processing) return;
    setProcessing(true);
    try {
      const db  = databaseRef.current;
      const ctx = getContext();
      if (ctx?.collections?.properties?.update) {
        await ctx.collections.properties.update(resolvedId, {
          status:    "Vendido",
          clientId:  selectedClient?.id || property.clientId,
          updatedAt: new Date().toISOString(),
        });
        if (ctx.syncManager) ctx.syncManager.markRecentlyModified(resolvedId);
      } else {
        await db?.updatePropertyStatus?.(resolvedId, "Vendido", selectedClient?.id || property.clientId, {});
      }
      setProperty((prev) => prev ? { ...prev, status: "Vendido" } : prev);
      await refreshProperty();
      notifySystem({
        type: "success",
        title: "\u{1F389} Venda registrada!",
        message: selectedClient ? `Imóvel vendido para ${selectedClient.name}` : "Venda registrada no financeiro",
      });
    } catch (_) {
      notifySystem({ type: "danger", title: "Erro ao registrar venda" });
    } finally {
      setProcessing(false);
      setShowSaleModal(false);
      setSelectedClient(null);
    }
  }, [property, processing, resolvedId, selectedClient, refreshProperty, notifySystem, getContext]);

  const handleLinkClient = useCallback(async (client) => {
    if (!property || !client) return;
    setLinkedClientsList((prev) => {
      const already = prev.some((c) => c.id === client.id);
      return already ? prev : [...prev, client];
    });
    try {
      const db  = databaseRef.current;
      const ctx = getContext();
      const newLinkedClients = [...(property.linkedClients || [])];
      if (!newLinkedClients.includes(client.id)) newLinkedClients.push(client.id);
      if (ctx?.collections?.properties?.update) {
        await ctx.collections.properties.update(resolvedId, {
          clientId:      client.id,
          linkedClients: newLinkedClients,
          updatedAt:     new Date().toISOString(),
        });
        if (ctx.syncManager) ctx.syncManager.markRecentlyModified?.(resolvedId);
      } else {
        await db?.linkPropertyToClient?.(resolvedId, client.id);
      }
      await refreshProperty();
      notifySystem({ type: "success", title: "Cliente vinculado!", message: `${client.name} vinculado ao imóvel` });
    } catch (_) {
      setLinkedClientsList((prev) => prev.filter((c) => c.id !== client.id));
      notifySystem({ type: "danger", title: "Erro ao vincular cliente" });
    }
  }, [property, resolvedId, refreshProperty, notifySystem, getContext]);

  const handleUnlinkClient = useCallback(async () => {
    if (!property) return;
    const backup = [...linkedClientsList];
    setLinkedClientsList([]);
    try {
      const db  = databaseRef.current;
      const ctx = getContext();
      if (ctx?.collections?.properties?.update) {
        await ctx.collections.properties.update(resolvedId, {
          clientId:      null,
          linkedClients: [],
          updatedAt:     new Date().toISOString(),
        });
        if (ctx.syncManager) ctx.syncManager.markRecentlyModified?.(resolvedId);
      } else {
        await db?.unlinkProperty?.(resolvedId);
      }
      await refreshProperty();
      notifySystem({ type: "success", title: "Cliente desvinculado" });
    } catch (_) {
      setLinkedClientsList(backup);
      notifySystem({ type: "danger", title: "Erro ao desvincular" });
    }
  }, [property, resolvedId, linkedClientsList, refreshProperty, notifySystem, getContext]);

  // ── Loading / Error ───────────────────────────────────────────────────────
  if (loading) return <PropertySkeleton isMobile={isMobile} />;

  if (loadError || !property) {
    return (
      <div className={isMobile ? "p-3" : "p-6 max-w-7xl mx-auto"}>
        <div className="bg-white border border-rose-200 rounded-2xl shadow p-8 flex flex-col items-center gap-4 text-center">
          <div className="p-4 bg-rose-100 rounded-full"><Home className="w-8 h-8 text-rose-500" /></div>
          <div>
            <p className="font-semibold text-slate-800 text-lg">Imóvel não encontrado</p>
            <p className="text-slate-500 text-sm mt-1">O imóvel pode ainda estar sincronizando. Tente novamente.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={loadProperty}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
              <RefreshCw className="w-4 h-4" /> Tentar novamente
            </button>
            <button onClick={() => navigate("/admin/imoveis")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium hover:bg-gray-50">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className={isMobile ? "p-3 space-y-4 pb-10" : "p-6 max-w-7xl mx-auto space-y-8"}>
      <div className="flex justify-between items-center">
        <button onClick={navigateBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 group">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className={`font-medium ${isMobile ? "text-sm" : ""}`}>Voltar</span>
        </button>
        <div className="flex items-center gap-2 text-slate-700">
          <Home className="w-5 h-5" />
          <span className={`font-medium ${isMobile ? "text-sm" : ""}`}>
            {isMobile ? "Detalhes" : "Detalhes do Imóvel"}
          </span>
        </div>
      </div>

      <div
        className={`rounded-2xl text-white ${isMobile ? "flex flex-col gap-3 p-4" : "flex justify-between items-start flex-wrap gap-4 p-6"}`}
        style={{ backgroundColor: primaryColor }}
      >
        <div className="min-w-0 flex-1">
          <h1 className={`font-bold truncate ${isMobile ? "text-xl" : "text-3xl"}`}>{property.title}</h1>
          <p className="text-white/75 text-xs mt-1">{property.status || "Disponível"}</p>
        </div>
        {isMobile ? (
          <div className="self-end">
            <MobileActionsMenu
              canSell={canSell} property={property} copied={copied}
              handleShare={handleShare} toggleFeatured={toggleFeatured}
              handleSellClick={handleSellClick} handleWhatsApp={handleWhatsApp}
              exportPDF={exportPDF} handleCopyLink={handleCopyLink}
            />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <ActionBtn icon={copied ? Check : Copy} label={copied ? "Copiado!" : "Copiar Link"} onClick={handleCopyLink} color="bg-blue-500 hover:bg-blue-600" />
            <ActionBtn icon={Share2} label="Compartilhar" onClick={handleShare} color="bg-blue-500 hover:bg-blue-600" />
            <ActionBtn icon={Star} label={property.featured ? "Em destaque" : "Destacar"} onClick={toggleFeatured} color={property.featured ? "bg-yellow-400 hover:bg-yellow-500" : "bg-white/20 hover:bg-white/30"} />
            {canSell && <ActionBtn icon={TrendingUp} label="Vender" onClick={handleSellClick} color="bg-emerald-500 hover:bg-emerald-600" />}
            <ActionBtn icon={MessageCircle} label="WhatsApp" onClick={handleWhatsApp} color="bg-green-600 hover:bg-green-700" />
            <ActionBtn icon={Download} label="PDF" onClick={exportPDF} color="bg-indigo-500 hover:bg-indigo-600" />
          </div>
        )}
      </div>

      <ImageGallery
        images={property.images || []} activeImage={activeImage}
        onPrev={prevImage} onNext={nextImage} onSelect={setActiveImage}
        primaryColor={primaryColor}
        onAddImage={() => navigate(`/admin/imoveis/editar/${resolvedId}?tab=imagens`, { state: { from: "details", propertyId: resolvedId } })}
        isMobile={isMobile}
      />

      <div className={`grid ${isMobile ? "grid-cols-1 gap-4" : "grid-cols-2 gap-6"}`}>
        <InfoCard icon={MapPin} primaryColor={primaryColor} isMobile={isMobile}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Endereço</p>
          <h2 className="font-bold text-slate-900 mb-1">{property.address || "Não informado"}</h2>
          {property.city && (
            <p className="text-slate-500 text-sm">
              {property.city}{property.neighborhood ? ` — ${property.neighborhood}` : ""}
            </p>
          )}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Valor</p>
            <span className="font-bold text-3xl" style={{ color: primaryColor }}>{priceFormatted}</span>
          </div>
        </InfoCard>

        <InfoCard icon={Users} primaryColor={primaryColor} isMobile={isMobile}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Cliente Vinculado</p>
          {linkedClientsList.length > 0 ? (
            <>
              {linkedClientsList.map((client) => (
                <div key={client.id}
                  className="flex items-center gap-3 mb-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors"
                  onClick={() => navigate(`/clientes/${client.id}`)}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                    {client.name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-slate-900 truncate">{client.name}</h2>
                    {client.email && (
                      <p className="text-slate-500 text-xs flex items-center gap-1">
                        <Mail className="w-3 h-3 flex-shrink-0" /> {client.email}
                      </p>
                    )}
                    {client.phone && (
                      <p className="text-slate-500 text-xs flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3 flex-shrink-0" /> {client.phone}
                      </p>
                    )}
                  </div>
                  <Eye className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </div>
              ))}
              <button onClick={handleUnlinkClient} className="text-xs text-rose-600 hover:text-rose-700 flex items-center gap-1 mt-1">
                <X className="w-3 h-3" /> Desvincular
              </button>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                <Users className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm mb-2">Nenhum cliente vinculado</p>
              <button onClick={() => setShowLinkClientModal(true)}
                className="text-xs font-medium hover:underline" style={{ color: primaryColor }}>
                Vincular cliente →
              </button>
            </div>
          )}
        </InfoCard>
      </div>

      {(() => {
        const specs = [
          { key: "type", label: "Tipo", icon: Home, value: property?.type },
          { key: "bedrooms", label: "Quartos", icon: null, value: property?.bedrooms, suffix: Number(property?.bedrooms) === 1 ? "quarto" : "quartos" },
          { key: "bathrooms", label: "Banheiros", icon: null, value: property?.bathrooms, suffix: Number(property?.bathrooms) === 1 ? "banheiro" : "banheiros" },
          { key: "area", label: "Área", icon: Maximize2, value: property?.area, suffix: "m²", formatter: (v) => Number(v).toLocaleString("pt-BR") },
          { key: "garage", label: "Garagem", icon: null, value: property?.garage, suffix: Number(property?.garage) === 1 ? "vaga" : "vagas" },
          { key: "code", label: "Código", icon: null, value: property?.code, mono: true },
        ].filter((s) => s.value !== null && s.value !== undefined && s.value !== "");

        if (!specs.length) return null;

        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
          >
            <div className="px-5 md:px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}15` }}>
                <Home className="w-[18px] h-[18px]" style={{ color: primaryColor }} />
              </div>
              <h3 className="font-semibold text-slate-800 text-[15px] tracking-tight">Características do Imóvel</h3>
            </div>
            <div className={`p-5 md:p-6 grid gap-3 ${isMobile ? "grid-cols-2" : specs.length <= 3 ? "grid-cols-3" : "grid-cols-3 lg:grid-cols-4"}`}>
              {specs.map((spec, i) => {
                const Icon = spec.icon;
                const displayValue = spec.formatter ? spec.formatter(spec.value) : String(spec.value);
                return (
                  <motion.div key={spec.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.3 }}
                    className="group relative bg-slate-50/80 hover:bg-slate-50 rounded-xl p-3.5 transition-all duration-200 hover:shadow-sm border border-transparent hover:border-slate-200/60">
                    <div className="absolute top-0 left-3 right-3 h-[2px] rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ backgroundColor: primaryColor }} />
                    <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      {Icon && <Icon className="w-3 h-3 text-slate-400" />}{spec.label}
                    </p>
                    <p className={`font-semibold text-slate-800 text-sm truncate ${spec.mono ? "font-mono tracking-tight" : "tracking-tight"}`}
                      title={displayValue}>
                      {displayValue}
                      {spec.suffix && <span className="text-slate-400 font-normal text-xs ml-1">{spec.suffix}</span>}
                    </p>
                  </motion.div>
                );
              })}
            </div>
            <div className="h-0.5 w-full" style={{ backgroundColor: `${primaryColor}08` }} />
          </motion.div>
        );
      })()}

      {property.description && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
          <h3 className="font-semibold text-slate-800 text-lg mb-4">Descrição do Imóvel</h3>
          <p className="text-slate-600 whitespace-pre-wrap leading-relaxed text-sm md:text-base">{property.description}</p>
        </motion.div>
      )}

      {(iptuFormatted || condoFeeFormatted || property.neighborhood) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
          <h3 className="font-semibold text-slate-800 text-lg mb-4">Informações Adicionais</h3>
          <div className={`grid ${isMobile ? "grid-cols-1 gap-3" : "grid-cols-2 gap-4"}`}>
            {property.neighborhood && <div><p className="text-xs text-slate-500 mb-0.5">Bairro</p><p className="font-medium text-slate-800">{property.neighborhood}</p></div>}
            {iptuFormatted && <div><p className="text-xs text-slate-500 mb-0.5">IPTU</p><p className="font-medium text-slate-800">{iptuFormatted}</p></div>}
            {condoFeeFormatted && <div><p className="text-xs text-slate-500 mb-0.5">Condomínio</p><p className="font-medium text-slate-800">{condoFeeFormatted}</p></div>}
          </div>
        </motion.div>
      )}

      <div className="flex gap-3">
        <button onClick={() => navigate(`/admin/imoveis/editar/${resolvedId}`, { state: { from: "details", propertyId: resolvedId } })}
          className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 font-semibold py-3 text-sm transition-colors">
          <Edit3 className="w-4 h-4" /> Editar
        </button>
        <button onClick={() => setShowDeleteModal(true)} disabled={deleting}
          className="flex-1 rounded-xl bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center gap-2 font-semibold py-3 text-sm transition-colors disabled:opacity-50">
          {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {deleting ? "Excluindo..." : "Excluir"}
        </button>
      </div>

      <AnimatePresence>
        {showDeleteModal && (
          <ModalShell title="Excluir imóvel" tone="danger" onClose={() => setShowDeleteModal(false)} primaryColor={primaryColor} isMobile={isMobile}>
            <p className="text-slate-700 text-sm">
              Deseja realmente excluir <span className="font-semibold">{property.title}</span>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDeleteModal(false)} disabled={deleting}
                className="px-4 py-2 rounded-xl border text-sm hover:bg-gray-50 disabled:opacity-50">Cancelar</button>
              <button onClick={confirmDelete} disabled={deleting}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm disabled:opacity-50 flex items-center gap-2">
                {deleting && <Loader className="w-3 h-3 animate-spin" />}
                {deleting ? "Excluindo..." : "Excluir"}
              </button>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>

      <ClientPickerModal open={showClientModal} onClose={() => setShowClientModal(false)}
        onSelect={handleClientSelected} clients={clients} primaryColor={primaryColor}
        isMobile={isMobile} title="Selecionar Comprador" />

      <SaleConfirmationModal open={showSaleModal} onClose={() => { setShowSaleModal(false); setSelectedClient(null); }}
        onConfirm={handleConfirmSale} property={property} client={selectedClient}
        primaryColor={primaryColor} isMobile={isMobile} processing={processing} />

      <ClientPickerModal open={showLinkClientModal} onClose={() => setShowLinkClientModal(false)}
        onSelect={handleLinkClient} clients={clients} primaryColor={primaryColor}
        isMobile={isMobile} title="Vincular Cliente" />
    </div>
  );
}